/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { CollectionIdPreviewsuggestionsBody } from '../models';
import { InlineResponse20011 } from '../models';
import { Suggestion } from '../models';
import { SuggestionURL } from '../models';
/**
 * SelectionsApi - axios parameter creator
 * @export
 */
export const SelectionsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get the list of favorited suggestions in this collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdFavoriteSuggestionsGet: async (collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdFavoriteSuggestionsGet.');
            }
            const localVarPath = `/collections/{collection_id}/favorite-suggestions`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Add a preview suggestion to this collection
         * @param {CollectionIdPreviewsuggestionsBody} body URL for generating a preview suggestion
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdPreviewSuggestionsPost: async (body: CollectionIdPreviewsuggestionsBody, collection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling collectionsCollectionIdPreviewSuggestionsPost.');
            }
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdPreviewSuggestionsPost.');
            }
            const localVarPath = `/collections/{collection_id}/preview-suggestions`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get this collection's suggestions
         * @param {string} collection_id UUID for the collection
         * @param {string} [type] Suggestion type to return
         * @param {string} [sort] Sorting method to use
         * @param {number} [age] Maximum age (in days) of suggestions to return
         * @param {number} [limit] Maximum number of suggestions to return
         * @param {number} [offset] Starting position offset of the list of suggestions to return
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        collectionsCollectionIdSuggestionsGet: async (collection_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'collection_id' is not null or undefined
            if (collection_id === null || collection_id === undefined) {
                throw new RequiredError('collection_id','Required parameter collection_id was null or undefined when calling collectionsCollectionIdSuggestionsGet.');
            }
            const localVarPath = `/collections/{collection_id}/suggestions`
                .replace(`{${"collection_id"}}`, encodeURIComponent(String(collection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (type !== undefined) {
                localVarQueryParameter['type'] = type;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (age !== undefined) {
                localVarQueryParameter['age'] = age;
            }

            if (limit !== undefined) {
                localVarQueryParameter['limit'] = limit;
            }

            if (offset !== undefined) {
                localVarQueryParameter['offset'] = offset;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a selection
         * @param {string} selection_id UUID for the selection
         * @param {Array<string>} [include] List of fields to include in response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        selectionsSelectionIdGet: async (selection_id: string, include?: Array<string>, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'selection_id' is not null or undefined
            if (selection_id === null || selection_id === undefined) {
                throw new RequiredError('selection_id','Required parameter selection_id was null or undefined when calling selectionsSelectionIdGet.');
            }
            const localVarPath = `/selections/{selection_id}`
                .replace(`{${"selection_id"}}`, encodeURIComponent(String(selection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            if (include) {
                localVarQueryParameter['include'] = include.join(COLLECTION_FORMATS.csv);
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * Initially publish selection to all destinations or republish. Republishing may cause duplication in some destinations.
         * @summary Publish a selection
         * @param {string} selection_id UUID for the selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        selectionsSelectionIdPost: async (selection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'selection_id' is not null or undefined
            if (selection_id === null || selection_id === undefined) {
                throw new RequiredError('selection_id','Required parameter selection_id was null or undefined when calling selectionsSelectionIdPost.');
            }
            const localVarPath = `/selections/{selection_id}`
                .replace(`{${"selection_id"}}`, encodeURIComponent(String(selection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * Push changes only to destination where there is no risk of duplication
         * @summary Safe republish a selection
         * @param {string} selection_id UUID for the selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        selectionsSelectionIdPut: async (selection_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'selection_id' is not null or undefined
            if (selection_id === null || selection_id === undefined) {
                throw new RequiredError('selection_id','Required parameter selection_id was null or undefined when calling selectionsSelectionIdPut.');
            }
            const localVarPath = `/selections/{selection_id}`
                .replace(`{${"selection_id"}}`, encodeURIComponent(String(selection_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SelectionsApi - functional programming interface
 * @export
 */
export const SelectionsApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get the list of favorited suggestions in this collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdFavoriteSuggestionsGet(collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<SuggestionURL>>>> {
            const localVarAxiosArgs = await SelectionsApiAxiosParamCreator(configuration).collectionsCollectionIdFavoriteSuggestionsGet(collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Add a preview suggestion to this collection
         * @param {CollectionIdPreviewsuggestionsBody} body URL for generating a preview suggestion
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdPreviewSuggestionsPost(body: CollectionIdPreviewsuggestionsBody, collection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20011>>> {
            const localVarAxiosArgs = await SelectionsApiAxiosParamCreator(configuration).collectionsCollectionIdPreviewSuggestionsPost(body, collection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get this collection's suggestions
         * @param {string} collection_id UUID for the collection
         * @param {string} [type] Suggestion type to return
         * @param {string} [sort] Sorting method to use
         * @param {number} [age] Maximum age (in days) of suggestions to return
         * @param {number} [limit] Maximum number of suggestions to return
         * @param {number} [offset] Starting position offset of the list of suggestions to return
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdSuggestionsGet(collection_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<SuggestionURL>>>> {
            const localVarAxiosArgs = await SelectionsApiAxiosParamCreator(configuration).collectionsCollectionIdSuggestionsGet(collection_id, type, sort, age, limit, offset, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a selection
         * @param {string} selection_id UUID for the selection
         * @param {Array<string>} [include] List of fields to include in response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async selectionsSelectionIdGet(selection_id: string, include?: Array<string>, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Suggestion>>> {
            const localVarAxiosArgs = await SelectionsApiAxiosParamCreator(configuration).selectionsSelectionIdGet(selection_id, include, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * Initially publish selection to all destinations or republish. Republishing may cause duplication in some destinations.
         * @summary Publish a selection
         * @param {string} selection_id UUID for the selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async selectionsSelectionIdPost(selection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SelectionsApiAxiosParamCreator(configuration).selectionsSelectionIdPost(selection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * Push changes only to destination where there is no risk of duplication
         * @summary Safe republish a selection
         * @param {string} selection_id UUID for the selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async selectionsSelectionIdPut(selection_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await SelectionsApiAxiosParamCreator(configuration).selectionsSelectionIdPut(selection_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * SelectionsApi - factory interface
 * @export
 */
export const SelectionsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @summary Get the list of favorited suggestions in this collection
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdFavoriteSuggestionsGet(collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<SuggestionURL>>> {
            return SelectionsApiFp(configuration).collectionsCollectionIdFavoriteSuggestionsGet(collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Add a preview suggestion to this collection
         * @param {CollectionIdPreviewsuggestionsBody} body URL for generating a preview suggestion
         * @param {string} collection_id UUID for the collection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdPreviewSuggestionsPost(body: CollectionIdPreviewsuggestionsBody, collection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20011>> {
            return SelectionsApiFp(configuration).collectionsCollectionIdPreviewSuggestionsPost(body, collection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get this collection's suggestions
         * @param {string} collection_id UUID for the collection
         * @param {string} [type] Suggestion type to return
         * @param {string} [sort] Sorting method to use
         * @param {number} [age] Maximum age (in days) of suggestions to return
         * @param {number} [limit] Maximum number of suggestions to return
         * @param {number} [offset] Starting position offset of the list of suggestions to return
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async collectionsCollectionIdSuggestionsGet(collection_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<SuggestionURL>>> {
            return SelectionsApiFp(configuration).collectionsCollectionIdSuggestionsGet(collection_id, type, sort, age, limit, offset, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a selection
         * @param {string} selection_id UUID for the selection
         * @param {Array<string>} [include] List of fields to include in response
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async selectionsSelectionIdGet(selection_id: string, include?: Array<string>, options?: AxiosRequestConfig): Promise<AxiosResponse<Suggestion>> {
            return SelectionsApiFp(configuration).selectionsSelectionIdGet(selection_id, include, options).then((request) => request(axios, basePath));
        },
        /**
         * Initially publish selection to all destinations or republish. Republishing may cause duplication in some destinations.
         * @summary Publish a selection
         * @param {string} selection_id UUID for the selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async selectionsSelectionIdPost(selection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SelectionsApiFp(configuration).selectionsSelectionIdPost(selection_id, options).then((request) => request(axios, basePath));
        },
        /**
         * Push changes only to destination where there is no risk of duplication
         * @summary Safe republish a selection
         * @param {string} selection_id UUID for the selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async selectionsSelectionIdPut(selection_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return SelectionsApiFp(configuration).selectionsSelectionIdPut(selection_id, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SelectionsApi - object-oriented interface
 * @export
 * @class SelectionsApi
 * @extends {BaseAPI}
 */
export class SelectionsApi extends BaseAPI {
    /**
     * 
     * @summary Get the list of favorited suggestions in this collection
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SelectionsApi
     */
    public async collectionsCollectionIdFavoriteSuggestionsGet(collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<SuggestionURL>>> {
        return SelectionsApiFp(this.configuration).collectionsCollectionIdFavoriteSuggestionsGet(collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Add a preview suggestion to this collection
     * @param {CollectionIdPreviewsuggestionsBody} body URL for generating a preview suggestion
     * @param {string} collection_id UUID for the collection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SelectionsApi
     */
    public async collectionsCollectionIdPreviewSuggestionsPost(body: CollectionIdPreviewsuggestionsBody, collection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20011>> {
        return SelectionsApiFp(this.configuration).collectionsCollectionIdPreviewSuggestionsPost(body, collection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get this collection's suggestions
     * @param {string} collection_id UUID for the collection
     * @param {string} [type] Suggestion type to return
     * @param {string} [sort] Sorting method to use
     * @param {number} [age] Maximum age (in days) of suggestions to return
     * @param {number} [limit] Maximum number of suggestions to return
     * @param {number} [offset] Starting position offset of the list of suggestions to return
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SelectionsApi
     */
    public async collectionsCollectionIdSuggestionsGet(collection_id: string, type?: string, sort?: string, age?: number, limit?: number, offset?: number, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<SuggestionURL>>> {
        return SelectionsApiFp(this.configuration).collectionsCollectionIdSuggestionsGet(collection_id, type, sort, age, limit, offset, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a selection
     * @param {string} selection_id UUID for the selection
     * @param {Array<string>} [include] List of fields to include in response
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SelectionsApi
     */
    public async selectionsSelectionIdGet(selection_id: string, include?: Array<string>, options?: AxiosRequestConfig) : Promise<AxiosResponse<Suggestion>> {
        return SelectionsApiFp(this.configuration).selectionsSelectionIdGet(selection_id, include, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * Initially publish selection to all destinations or republish. Republishing may cause duplication in some destinations.
     * @summary Publish a selection
     * @param {string} selection_id UUID for the selection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SelectionsApi
     */
    public async selectionsSelectionIdPost(selection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SelectionsApiFp(this.configuration).selectionsSelectionIdPost(selection_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * Push changes only to destination where there is no risk of duplication
     * @summary Safe republish a selection
     * @param {string} selection_id UUID for the selection
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof SelectionsApi
     */
    public async selectionsSelectionIdPut(selection_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return SelectionsApiFp(this.configuration).selectionsSelectionIdPut(selection_id, options).then((request) => request(this.axios, this.basePath));
    }
}
